import { useEffect, useState } from 'react'
import { apiGet } from '../../services/api'
import { Building2, Users, CreditCard, TrendingUp } from 'lucide-react'
import { Link } from 'react-router-dom'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface DashboardStats {
  total_companies: number
  active_companies: number
  total_users: number
  total_indicators: number
}

interface Company {
  id: number
  name: string
  code: string
  is_active: boolean
  user_count?: number
}

export default function SuperadminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recentCompanies, setRecentCompanies] = useState<Company[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesRes, indicatorsRes] = await Promise.all([
          apiGet<{ items: Company[]; total: number }>('/companies'),
          apiGet<{ id: number }[]>('/kpi/indicators'),
        ])

        const companies = companiesRes.items || []
        const activeCompanies = companies.filter(c => c.is_active).length

        setStats({
          total_companies: companiesRes.total || companies.length,
          active_companies: activeCompanies,
          total_users: 0, // Will be fetched separately if needed
          total_indicators: indicatorsRes.length,
        })

        setRecentCompanies(companies.slice(0, 5))
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Host Dashboard</h1>
        <p className="text-gray-500 mt-1">Manage companies and system settings</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Companies</p>
              <p className="text-2xl font-bold">{stats?.total_companies || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Companies</p>
              <p className="text-2xl font-bold">{stats?.active_companies || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">KPI Indicators</p>
              <p className="text-2xl font-bold">{stats?.total_indicators || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <CreditCard className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Subscriptions</p>
              <p className="text-2xl font-bold">--</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/superadmin/companies"
              className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-center transition-colors"
            >
              <Building2 className="h-8 w-8 mx-auto text-gray-600 mb-2" />
              <span className="text-sm font-medium">Manage Companies</span>
            </Link>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <CreditCard className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <span className="text-sm font-medium text-gray-400">Subscriptions</span>
              <p className="text-xs text-gray-400 mt-1">Coming soon</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Recent Companies</h3>
            <Link to="/superadmin/companies" className="text-sm text-primary-600 hover:underline">
              View all
            </Link>
          </div>
          {recentCompanies.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No companies yet</p>
          ) : (
            <div className="space-y-3">
              {recentCompanies.map((company) => (
                <div key={company.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <Building2 className="h-4 w-4 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-medium">{company.name}</p>
                      <p className="text-sm text-gray-500">{company.code}</p>
                    </div>
                  </div>
                  <span className={company.is_active ? 'badge-success' : 'badge-danger'}>
                    {company.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
